import { skipToken } from "@reduxjs/toolkit/query"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { CURRENCY_DISPLAY_ORDER } from "@/helpers/currencyPresentation"
import { resolveMobCardImage } from "@/helpers/mobImage"
import { isBelowBattleEntryHp } from "@/helpers/vitals"
import { useCatalogs } from "@/hooks/useCatalogs"
import {
  buildLootStrip,
  afterEngagementOpen,
  buildMobDoll,
  buildMobVitals,
  deriveAttackView,
  devWinEnabled,
  groupMobStats,
  isSameCell,
  nextTappedMobFighting,
  refusalAfterChange,
  showsLoot
} from "@/hooks/useMobCard/helpers"
import {
  IAttackViewInput,
  IMobCardAction,
  IMobCardDoll,
  IMobCardView,
  IMobLootTile,
  IMobStatGroups,
  IRefusalContext
} from "@/hooks/useMobCard/types"
import { usePlayerStanding } from "@/hooks/usePlayerStanding"
import { IMobCardParams } from "@/modals/constructor/mobs/types"
import { useModals } from "@/modals/hooks"
import { useHeroVitalsContext } from "@/providers/HeroVitals"
import { useAppDispatch, useAppSelector } from "@/store"
import { TAGS } from "@/store/api"
import {
  useGetOpenBattleQuery,
  useStartBattleMutation
} from "@/store/apis/battleAPI/battle.api"
import { BATTLE_DENIAL_REASON } from "@/store/apis/battleAPI/battle.types"
import { heroAPI } from "@/store/apis/heroAPI/hero.api"
import {
  attackRefusalOf,
  IAttackRefusal
} from "@/store/apis/mobsAPI/attackRefusal"
import {
  isMobStillFighting,
  kindAvailability
} from "@/store/apis/mobsAPI/cellAvailability"
import {
  useDevAttackMobMutation,
  useDevLeaveEngagementMutation,
  useDevWinEngagementMutation,
  useGetCellMobsQuery,
  useGetMobCardQuery,
  useGetOpenEngagementQuery
} from "@/store/apis/mobsAPI/mobs.api"
import { MOB_ACTION_DENIAL_REASON } from "@/store/apis/mobsAPI/mobs.types"
import { AppSelectors } from "@/store/slices/app"
import { IVitalView } from "@/types/vitals.types"

const EMPTY_VITALS: IVitalView[] = []
const EMPTY_DOLL: IMobCardDoll = { layout: [], itemsByPosition: {} }
const EMPTY_STRIP: IMobLootTile[] = []
const EMPTY_STAT_GROUPS: IMobStatGroups = {
  attributes: [],
  modifiers: [],
  derived: []
}

const busyOnly = ({ isLoading }: { isLoading: boolean }) => ({ isLoading })

export const useMobCard = (params: IMobCardParams): IMobCardView => {
  const { typeCode, level, fromFight, mobId } = params
  const { mapId, x, y } = params.cell
  const isDevBuild = useAppSelector(AppSelectors.selectIsDevEnv())
  const dispatch = useAppDispatch()
  const { closeModal, openModal } = useModals()
  const { slotLayout, stats: statCatalog, currencies } = useCatalogs()
  const { i18n } = useTranslation()
  const locale = i18n.language
  const standing = usePlayerStanding()
  const hpTooLow = isBelowBattleEntryHp(useHeroVitalsContext())

  const {
    card,
    isError,
    refetch: refetchCard
  } = useGetMobCardQuery(
    { typeCode, level },
    {
      selectFromResult: ({ currentData, isError }) => ({
        card: currentData,
        isError
      })
    }
  )
  const { refetch: refetchOpenBattle } = useGetOpenBattleQuery(undefined, {
    selectFromResult: () => ({})
  })

  const [staleFight, setStaleFight] = useState(false)
  const { staleEngagementId } = useGetOpenEngagementQuery(
    staleFight ? undefined : skipToken,
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isFetching }) => ({
        staleEngagementId:
          currentData === undefined || isFetching
            ? undefined
            : (currentData.engagement?.id ?? null)
      })
    }
  )

  const { availability, mobFighting } = useGetCellMobsQuery(params.cell, {
    selectFromResult: ({ currentData }) => ({
      availability: kindAvailability(currentData, typeCode, level),
      mobFighting: isMobStillFighting(currentData, mobId)
    })
  })
  const tappedMobFightingRef = useRef(fromFight)
  const tappedMobFighting = nextTappedMobFighting(
    tappedMobFightingRef.current,
    fromFight,
    mobFighting
  )
  tappedMobFightingRef.current = tappedMobFighting

  const [startBattle, { isLoading: startBusy }] = useStartBattleMutation({
    selectFromResult: busyOnly
  })
  const [devLeaveEngagement, { isLoading: leaveBusy }] =
    useDevLeaveEngagementMutation({ selectFromResult: busyOnly })
  const [devAttackMob, { isLoading: devAttackBusy }] = useDevAttackMobMutation({
    selectFromResult: busyOnly
  })
  const [devWinEngagement, { isLoading: devWinBusy }] =
    useDevWinEngagementMutation({ selectFromResult: busyOnly })
  const busy =
    startBusy ||
    leaveBusy ||
    devAttackBusy ||
    devWinBusy ||
    (staleFight && staleEngagementId === undefined)

  const [refusal, setRefusal] = useState<IAttackRefusal | null>(null)
  const previousContext = useRef<IRefusalContext>({
    ...standing,
    availability,
    hpTooLow
  })

  useEffect(() => {
    const next: IRefusalContext = { ...standing, availability, hpTooLow }
    const previous = previousContext.current
    previousContext.current = next
    setRefusal((current) => refusalAfterChange(current, previous, next))
  }, [standing, availability, hpTooLow])

  useEffect(() => {
    setRefusal(null)
  }, [typeCode, level, mapId, x, y])

  const settleEngagementOpen = useCallback(async () => {
    try {
      const { battle } = await refetchOpenBattle().unwrap()
      const step = afterEngagementOpen(battle, isDevBuild)
      if (step === "GATE") {
        closeModal("mobCard")
      } else if (step === "STALE_FIGHT") {
        setStaleFight(true)
      } else {
        setRefusal("FAILED")
      }
    } catch {
      setRefusal("FAILED")
    }
  }, [refetchOpenBattle, isDevBuild, closeModal])

  const onAttackRefused = useCallback(
    (error: unknown) => {
      const outcome = attackRefusalOf(error)
      if (outcome === MOB_ACTION_DENIAL_REASON.ENGAGEMENT_OPEN) {
        void settleEngagementOpen()
        return
      }
      if (outcome === BATTLE_DENIAL_REASON.HP_TOO_LOW) {
        dispatch(heroAPI.util.invalidateTags([TAGS.HERO]))
      }
      setRefusal(outcome)
    },
    [settleEngagementOpen, dispatch]
  )

  const onAttack = useCallback(() => {
    setRefusal(null)
    startBattle({ mapId, x, y, typeCode, level })
      .unwrap()
      .then((view) => {
        closeModal("mobCard")
        openModal("battleScreen", { battleId: view.id })
      }, onAttackRefused)
  }, [
    startBattle,
    closeModal,
    openModal,
    onAttackRefused,
    mapId,
    x,
    y,
    typeCode,
    level
  ])

  const onDevWin = useCallback(() => {
    setRefusal(null)
    devAttackMob({ mapId, x, y, typeCode, level })
      .unwrap()
      .then(
        (engagement) =>
          devWinEngagement(engagement.id)
            .unwrap()
            .then(
              ({ kill }) => {
                closeModal("mobCard")
                openModal("mobReward", { killId: kill.id })
              },
              () => {
                setRefusal("FAILED")
                setStaleFight(true)
              }
            ),
        onAttackRefused
      )
  }, [
    devAttackMob,
    devWinEngagement,
    closeModal,
    openModal,
    onAttackRefused,
    mapId,
    x,
    y,
    typeCode,
    level
  ])

  const onLeave = useCallback(() => {
    if (staleEngagementId === undefined) {
      return
    }
    setRefusal(null)
    if (staleEngagementId === null) {
      setStaleFight(false)
      return
    }
    devLeaveEngagement(staleEngagementId)
      .unwrap()
      .then(
        () => setStaleFight(false),
        () => setRefusal("FAILED")
      )
  }, [staleEngagementId, devLeaveEngagement])

  const onCardCell = isSameCell(standing.standing, params.cell)

  const action = useMemo((): IMobCardAction => {
    const input: IAttackViewInput = {
      isMoving: standing.isMoving,
      onCardCell,
      tappedMobFighting,
      availability,
      busy,
      refusal,
      hpTooLow,
      staleFight
    }
    const view = deriveAttackView(input)
    if (view.kind !== "attack") {
      return { ...view, onLeave }
    }
    const devWin = devWinEnabled(input, isDevBuild)
    return {
      ...view,
      onAttack,
      devWin: devWin === null ? null : { enabled: devWin, onDevWin }
    }
  }, [
    standing.isMoving,
    onCardCell,
    tappedMobFighting,
    availability,
    busy,
    refusal,
    hpTooLow,
    staleFight,
    isDevBuild,
    onAttack,
    onDevWin,
    onLeave
  ])

  const vitals = useMemo(
    () => (card ? buildMobVitals(card.stats) : EMPTY_VITALS),
    [card]
  )
  const doll = useMemo(
    () => (card ? buildMobDoll(card.doll, slotLayout) : EMPTY_DOLL),
    [card, slotLayout]
  )
  const lootStrip = useMemo(
    () =>
      card
        ? buildLootStrip(card, currencies, locale, CURRENCY_DISPLAY_ORDER)
        : EMPTY_STRIP,
    [card, currencies, locale]
  )
  const statGroups = useMemo(
    () => (card ? groupMobStats(card.stats, statCatalog) : EMPTY_STAT_GROUPS),
    [card, statCatalog]
  )
  const imageUrl = useMemo(() => resolveMobCardImage(typeCode), [typeCode])

  const retry = useCallback(() => {
    void refetchCard()
  }, [refetchCard])

  return {
    status: card ? "ready" : isError ? "error" : "loading",
    retry,
    typeCode,
    level,
    vitals,
    imageUrl,
    doll,
    lootStrip,
    showLoot: showsLoot(lootStrip),
    statGroups,
    action
  }
}
